import React, { createContext, useContext, useState, useEffect } from 'react';
import { useBusiness } from './BusinessContext';
import { useAuth } from './AuthContext';
import api from '../services/api';

const PermissionsContext = createContext();

// Owner-only modules - staff can NEVER access these
const OWNER_ONLY_MODULES = [
  // Staff tracking (owner views)
  'staff_map',
  'staff_locations', 
  'staff_tracking',
  // Business analytics & intelligence
  'business_analytics',
  'profit_loss',
  // Finance & Billing
  'cash_bank',
  'billing',
  'recurring_invoices',
  'debit_notes',
  // Purchases (all)
  'purchase_orders',
  'purchase_bills',
  'payments_made',
  'vendor_credits',
  'expenses',
  // Admin/Settings
  'gst_filing',
  'settings',
  'users_roles',
  // Team admin
  'staff_management',
  'payroll',
];

export const PermissionsProvider = ({ children }) => {
  const { activeBusiness } = useBusiness();
  const { user } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [assignments, setAssignments] = useState({ customers: [], areas: [], stores: [] });
  const [isOwner, setIsOwner] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [staffId, setStaffId] = useState(null);
  const [ownerOnlyModules, setOwnerOnlyModules] = useState(OWNER_ONLY_MODULES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (activeBusiness?.id && user) {
      fetchPermissions();
    } else {
      setLoading(false);
    }
  }, [activeBusiness, user]);

  const fetchPermissions = async () => {
    try {
      const { data } = await api.get('/permissions/my-permissions');
      setPermissions(data.permissions || {});
      setAssignments(data.assignments || { customers: [], areas: [], stores: [] });
      setIsOwner(data.is_owner || false);
      setUserRole(data.role || 'MEMBER');
      setIsStaff(data.role === 'STAFF');
      setIsActive(data.is_active !== false);
      setStaffId(data.staff_id || null);
      setOwnerOnlyModules(data.owner_only_modules || OWNER_ONLY_MODULES);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      // Fallback: assume owner if API fails
      setIsOwner(true);
      setIsStaff(false);
      setUserRole('OWNER');
      setPermissions({});
      setAssignments({ customers: [], areas: [], stores: [] });
    } finally {
      setLoading(false);
    }
  };

  // Check if module is owner-only (staff blocked)
  const isOwnerOnlyModule = (module) => {
    return ownerOnlyModules.includes(module);
  };

  const hasPermission = (module, action) => {
    // Owners always have full access
    if (isOwner) return true;

    // Staff blocked from owner-only modules
    if (isStaff && isOwnerOnlyModule(module)) return false;

    // Check if permission exists
    return permissions?.[module]?.[action] === true;
  };

  // Check if user can VIEW a module (for sidebar visibility)
  const canViewModule = (module) => {
    // Owner always sees all
    if (isOwner) return true;

    // Staff blocked from owner-only modules
    if (isStaff && isOwnerOnlyModule(module)) return false;

    // Check view permission
    return permissions?.[module]?.view === true;
  };

  const canView = (module) => hasPermission(module, 'view');
  const canCreate = (module) => hasPermission(module, 'create');
  const canEdit = (module) => hasPermission(module, 'edit');
  const canDelete = (module) => hasPermission(module, 'delete');
  const canExport = (module) => hasPermission(module, 'export');
  const canApprove = (module) => hasPermission(module, 'approve');

  // Check if staff can access specific customer (by assignment)
  const canAccessCustomer = (customerId) => {
    // Owner has access to all
    if (isOwner) return true;
    
    // Staff needs assignment
    if (!assignments.customers || assignments.customers.length === 0) {
      return false; // No assignments = no access
    }
    
    return assignments.customers.includes(customerId);
  };

  // Check if staff can access specific area
  const canAccessArea = (areaName) => {
    if (isOwner) return true;
    
    if (!assignments.areas || assignments.areas.length === 0) {
      return false;
    }
    
    return assignments.areas.includes(areaName);
  };

  // Get list of accessible customer IDs for staff
  const getAccessibleCustomerIds = () => {
    if (isOwner) return null; // null means all access
    return assignments.customers || [];
  };

  const value = {
    permissions,
    assignments,
    isOwner,
    isStaff,
    isActive,
    userRole,
    staffId,
    loading,
    ownerOnlyModules,
    // Permission checks
    hasPermission,
    canViewModule,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canExport,
    canApprove,
    // Data scope checks
    canAccessCustomer,
    canAccessArea,
    getAccessibleCustomerIds,
    isOwnerOnlyModule,
    // Refresh
    refreshPermissions: fetchPermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};
